import { useState } from 'react';
import { useLocation } from 'wouter';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { HowToPlay } from '@/components/ui/how-to-play';
import { BookOpen } from 'lucide-react';
import { GameExplanationDialog } from './GameExplanationDialog';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';

// Default deck URL for card images
const DEFAULT_DECK_URL = 'https://teacherwilliam.sgp1.cdn.digitaloceanspaces.com/spar/deck/free/default';

export function LearnTabContent() {
  const [showTutorialDialog, setShowTutorialDialog] = useState(false);
  const [_, setLocation] = useLocation();
  const { toast } = useToast();
  
  // Create practice game mutation
  const createPracticeGame = useMutation({
    mutationFn: async () => {
      const res = await apiRequest('POST', '/api/game-rooms/practice');
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || 'Failed to create practice game');
      }
      return res.json();
    },
    onSuccess: (data) => {
      toast({
        title: 'Practice Game Created',
        description: 'Starting tutorial match against AI',
      });
      setLocation(`/game/${data.id}`);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create practice game',
        variant: 'destructive',
      });
    },
  });

  const handleStartPractice = () => {
    // Close the tutorial dialog
    setShowTutorialDialog(false);
    
    // Create a practice game against AI
    createPracticeGame.mutate();
  };

  const handleSkipTutorial = () => {
    setShowTutorialDialog(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold theme-text-primary flex items-center">
          <BookOpen className="h-5 w-5 mr-2" />
          Learn to Play
        </h2>
        <Button 
          onClick={() => setShowTutorialDialog(true)}
          className="bg-primary hover:bg-primary/90"
        >
          Open Card Tutorial
        </Button>
      </div>
      
      {/* Original HowToPlay component */}
      <HowToPlay />
      
      {/* Tutorial dialog */}
      <GameExplanationDialog 
        isOpen={showTutorialDialog}
        onOpenChange={setShowTutorialDialog}
        onStartPractice={handleStartPractice}
        onSkip={handleSkipTutorial}
        defaultDeckUrl={DEFAULT_DECK_URL}
      />
    </div>
  );
}